/**
 * TRBUD-401 Part B: the lists behind the Reports figures that had none.
 *
 * Each list is read by the rule that produced its figure, never by a second
 * spelling of it, so a list and the number it opens from add up by construction.
 * Browser-safe: types and constants only.
 */
import type { CashForecastMoney } from "./cash-forecast.js";
import type { ReportJobFilter } from "./category-period.js";
import type { SpendSegmentSelection } from "./spend-segment.js";

/* ── labor ───────────────────────────────────────────────────────── */

/**
 * One row of the Labor ROI figures. `currency` is the card's currency, or null
 * for the hours MaintainX imported at no rate; `includeUnpriced` adds those
 * hours to a priced row (the card's "Total labor").
 */
export interface LabourLinesSelection {
  timeKind: "repair_labour" | "admin_processing" | null;
  currency: string | null;
  includeUnpriced: boolean;
  start: string | null;
  end: string;
  filters: ReportJobFilter;
}

/** One person, one worked day, one job: the grain the Labor ROI card adds up. */
export interface LabourLine {
  key: string;
  workOrderId: string;
  workOrderNumber: string;
  title: string | null;
  person: string;
  date: string;
  repairMinutes: number;
  adminMinutes: number;
  /** Null for MaintainX hours imported at no rate. */
  currency: string | null;
  value: string | null;
  source: string;
}

export interface LabourLinesResponse {
  selection: LabourLinesSelection;
  minutes: number;
  /** Posted value in `selection.currency`; "0.00" for unpriced hours alone. */
  value: string;
  lineCount: number;
  workOrders: number;
  lines: LabourLine[];
  hasMore: boolean;
}

/* ── created vs completed ────────────────────────────────────────── */

/**
 * The Created vs completed tile's counts. `completed` is both completion counts
 * together, as the tile prints it; the other two are its parts.
 */
export const reportActivityCohorts = ["opened", "completed", "completed_from_opened", "completed_from_earlier"] as const;
export type ReportActivityCohort = (typeof reportActivityCohorts)[number];

export const reportActivityCohortLabels: Record<ReportActivityCohort, string> = {
  opened: "Opened in period",
  completed: "Completed in period",
  completed_from_opened: "Completed, opened in period",
  completed_from_earlier: "Completed, opened earlier",
};

export interface ActivityWorkOrdersSelection {
  cohort: ReportActivityCohort;
  start: string | null;
  end: string;
  filters: ReportJobFilter;
}

export interface ActivityWorkOrdersResponse {
  selection: ActivityWorkOrdersSelection;
  count: number;
  rows: Array<{
    workOrderId: string;
    number: string;
    title: string | null;
    openedAt: string;
    /** The completion the tile counted, or null for an opened work order not yet complete. */
    completedAt: string | null;
    imported: boolean;
  }>;
  hasMore: boolean;
}

/* ── cash ────────────────────────────────────────────────────────── */

/** Every group the cash panel prints, by the cash forecast's own classification. */
export const cashForecastCohortKeys = [
  "realized", "unrealized", "untracked_open", "untracked_completed",
  "open_backlog", "open_backlog_30", "open_backlog_60", "open_backlog_90", "undated_realized",
] as const;
export type CashForecastCohortKey = (typeof cashForecastCohortKeys)[number];

export const cashForecastCohortLabels: Record<CashForecastCohortKey, string> = {
  realized: "Realized in this window",
  unrealized: "Unrealized in this window",
  untracked_open: "Untracked open",
  untracked_completed: "Untracked completed",
  open_backlog: "All valued open jobs",
  open_backlog_30: "Open more than 30 days",
  open_backlog_60: "Open more than 60 days",
  open_backlog_90: "Open more than 90 days",
  undated_realized: "Recorded costs with unknown service date",
};

export interface CashForecastJobsSelection {
  cohort: CashForecastCohortKey;
  start: string | null;
  end: string;
  filters: ReportJobFilter;
}

export interface CashForecastJobsResponse {
  selection: CashForecastJobsSelection;
  jobs: number;
  /** The group's figures, exactly as the panel prints them. */
  amounts: CashForecastMoney[];
  rows: Array<{
    workOrderId: string;
    number: string;
    title: string | null;
    opened: string;
    closed: string | null;
    ageDays: number;
    amounts: CashForecastMoney[];
  }>;
  hasMore: boolean;
}

/* ── a figures-table cell that opens a list ──────────────────────── */

/**
 * What a click on one value of a KPI figures table opens, with the figure it
 * opens from, so the list can say when its own total has moved since.
 */
export type KpiDrill =
  | { kind: "spend"; selection: SpendSegmentSelection; amount: string }
  | { kind: "labour"; selection: LabourLinesSelection; minutes: number; value: string };

/** The window and filters a card was read over, for the drills its cells carry. */
export interface ReportDrillScope {
  start: string | null;
  end: string;
  filters: ReportJobFilter;
}
